/**
 * @module config/campaign
 * @description Single source of truth for organization- and campaign-level
 * facts. Everything the client may change between events lives here, so no
 * copy or figure is duplicated across components. Wording that differs
 * between the Gala and Campaign editions lives in config/editions.ts.
 *
 * Sources: the client's print PDF (_sources/) and theafj.org (scraped
 * 2026-09-30) for the EIN, the Neon CRM form URLs and the gala details;
 * Samantha's giving-page draft (_sources/AFJ Digital Giving Pledge Page
 * Postcard vs1.10.1.26.pdf, 2026-10-01) for the campaign gift options and
 * the "About your gift" disclosure.
 */
import type { CauseArea, PaymentIntent } from "../domain/types";

export interface CampaignConfig {
    campaignName: string;
    organizationName: string;
    eventName: string;
    eventDateLabel: string;
    eventVenue: string;
    matchCap: number;
    matchRatioLabel: string;
    pledgeDeadlineLabel: string;
    ein: string;
    /** Neon CRM hosted form that processes the actual payment. We never touch card data. */
    neonDonationUrl: string;
    siteUrl: string;
    siteLabel: string;
    mailingAddress: string;
    phone: string;
    /**
     * Base URL printed into QR codes and share links. `null` = the site the
     * app is currently served from. Set it once the production domain is
     * known (KI #5) so a print kit generated from a preview still points home.
     */
    publicAppUrl: string | null;
}

/** "https://x.dev" → "https://x.dev/"; empty or missing → null. */
function normalizeBaseUrl(value: string | undefined): string | null {
    if (!value) return null;
    return value.endsWith("/") ? value : `${value}/`;
}

export const CAMPAIGN: CampaignConfig = {
    campaignName: "To Jamaica, With Love",
    organizationName: "The American Friends of Jamaica",
    eventName: "2026 Hummingbird Gala",
    eventDateLabel: "Friday, October 16, 2026",
    eventVenue: "The Plaza · New York City",
    matchCap: 100_000,
    matchRatioLabel: "1:1",
    pledgeDeadlineLabel: "Dec 31, 2026",
    ein: "13-3115102",
    // Dedicated Neon form for this campaign, supplied by Griffin Sabatini (AFJ Operations
    // Manager) on 2026-10-23 (KI #11, wired in by KI #30). campaignId=737 tags every gift
    // to "To Jamaica, With Love" in Neon. Used verbatim as a link target: callers must not
    // append their own query string with "?", because this URL already carries one.
    neonDonationUrl: "https://theafj.app.neoncrm.com/forms/to-jamaica-with-love?campaignId=737",
    siteUrl: "https://theafj.org",
    siteLabel: "theafj.org",
    mailingAddress: "1697 Broadway, Suite 501 · New York, NY 10019",
    phone: "(212) 265-2550",
    // Each deploy script in package.json states its own public address: build:production
    // → https://love.theafj.org/ (AFJ option C, 2026-10-23; KI #29), build:preview → the
    // afj-tjwl.pages.dev preview, build:portrait → its review project. null (plain
    // `npm run build`, local dev) falls back to the current origin. Side-effect: a print kit
    // generated from a preview build prints QR codes that point to that preview.
    publicAppUrl: normalizeBaseUrl(import.meta.env.VITE_PUBLIC_APP_URL),
};

/** Human labels for section 2 ("How I'll give"), in print order. */
export const PAYMENT_INTENT_LABELS: Record<PaymentIntent, string> = {
    "pay-tonight": "Paid tonight via the QR code",
    "pledge-by-deadline": `Pledge, paid by ${CAMPAIGN.pledgeDeadlineLabel}`,
    "give-online": "Give securely online",
    "mail-check": "Mail a check",
    "daf-grant": "Give through a donor-advised fund",
    "call-me": "Please call me",
};

/** Intents that continue to AFJ's Neon CRM payment form after the pledge is recorded. */
export const ONLINE_PAYMENT_INTENTS: ReadonlySet<PaymentIntent> = new Set<PaymentIntent>(["pay-tonight", "give-online"]);

/** Human labels for section 4, in print order. */
export const CAUSE_LABELS: Record<CauseArea, string> = {
    education: "Education",
    health: "Health",
    communities: "Communities",
    environment: "Environment",
};

/** "About your gift" disclosure, verbatim from Samantha's giving-page draft (2026-10-01). */
export const ABOUT_YOUR_GIFT =
    "AFJ uses gifts where Jamaica needs them most, including reasonable costs of carrying out its charitable work. " +
    "The examples and interests on this page do not designate gifts to a specific project. AFJ's latest filed financial " +
    "report is available on request from AFJ, 1697 Broadway, Suite 501, New York, NY 10019, or from the New York Attorney " +
    "General at charitiesnys.com. For information about charities, visit ag.ny.gov or call (212) 416-8686. AFJ makes " +
    "grants to other organizations; a list of recipients from the past 12 months is available from AFJ on request.";

/** Formats whole dollars as "$1,000" — shared by every money display. */
export const formatUsd = (value: number): string =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

/** "1 friend has given" / "3 friends have given" — shared by every count display. */
export const friendsHaveGiven = (count: number): string => `${count === 1 ? "friend has" : "friends have"} given`;
